import { memo, useState, useEffect, useCallback } from 'react';
import { Text, Chip } from '@Sage/hoi-poi-ui';
import { formatDate } from 'utils/dates';
import { getLiteral } from 'utils/getLiteral';
import { FuzzyMap } from 'utils/fuzzy';
import { getEntityValue } from 'services/Fuzzy';
import { getDayWeekOptions, getMonthOptions, shouldShowEntity } from '../../../../utils';

const ConditionBoxValue = memo(({ field, condition, operator, entityFieldsMap }) => {
    const getValueType = useCallback(() => {
        const value = condition?.parameters?.value;
        switch (field?.type) {
            case 'percentage':
                switch (operator) {
                    case 'number_between':
                    case 'number_not_between':
                        return `${condition?.parameters?.valueFrom}% - ${condition?.parameters?.valueTo}%`;
                    default:
                        return `${value}%`;
                }
            case 'datetime':
                switch (operator) {
                    case 'datetime_in_range':
                    case 'datetime_not_in_range':
                        return `${formatDate(condition?.parameters?.valueFrom)} - ${formatDate(condition?.parameters?.valueTo)}`;
                    case 'datetime_last_n_days':
                    case 'datetime_next_n_days':
                        return value;
                    case 'datetime_day_of_week':
                        const dayOptions = getDayWeekOptions();
                        return dayOptions.find((option) => option.value === value)?.label;
                    case 'datetime_month':
                        const monthOptions = getMonthOptions();
                        return monthOptions.find((option) => option.value === value)?.label;
                    default:
                        return value ? formatDate(value) : '';
                }
            case 'bit':
                let valueLiteral;
                switch (value) {
                    case true:
                        valueLiteral = getLiteral('cfm_label_yes');
                        break;
                    case false:
                        valueLiteral = getLiteral('cfm_label_no');
                        break;
                }
                return valueLiteral;
            case 'int':
            case 'decimal':
            case 'currency':
                const parseStringWithVariables = (str) => {
                    const parts = [];
                    let lastIndex = 0;
                    const variableRegex = /\{\{([^}]+)\}\}/g;
                    let match;

                    while ((match = variableRegex.exec(str)) !== null) {
                        // Add text before the variable
                        if (match.index > lastIndex) {
                            parts.push(str.substring(lastIndex, match.index));
                        }

                        // Add the variable as a chip
                        const variableKey = match[1]?.trim();

                        const fieldInfo = entityFieldsMap[variableKey];
                        if (fieldInfo) {
                            parts.push(
                                <Chip key={match.index} icon={fieldInfo.icon} isFilled>
                                    {fieldInfo.label}
                                </Chip>,
                            );
                        } else {
                            // Fallback if field not found in map
                            parts.push(
                                <Chip key={match.index} isFilled>
                                    {variableKey}
                                </Chip>,
                            );
                        }

                        lastIndex = match.index + match[0].length;
                    }

                    // Add remaining text after the last variable
                    if (lastIndex < str.length) {
                        parts.push(str.substring(lastIndex));
                    }

                    return parts;
                };

                switch (operator) {
                    case 'empty':
                    case 'not_empty':
                        return null;
                    case 'number_between':
                    case 'number_not_between':
                        return (
                            <div className="fm-automation-builder__chip-values">
                                {parseStringWithVariables(condition?.parameters?.valueFrom).map(
                                    (part, index) =>
                                        typeof part === 'string' ? (
                                            <Text key={index}>{part}</Text>
                                        ) : (
                                            part
                                        ),
                                )}
                                -
                                {parseStringWithVariables(condition?.parameters?.valueTo).map(
                                    (part, index) =>
                                        typeof part === 'string' ? (
                                            <Text key={index}>{part}</Text>
                                        ) : (
                                            part
                                        ),
                                )}
                            </div>
                        );
                    default:
                        return (
                            <div className="fm-automation-builder__chip-values">
                                {parseStringWithVariables(value).map((part, index) =>
                                    typeof part === 'string' ? (
                                        <Text key={index}>{part}</Text>
                                    ) : (
                                        part
                                    ),
                                )}
                            </div>
                        );
                }
            default:
                switch (operator) {
                    case 'number_between':
                    case 'number_not_between':
                        return `${condition?.parameters?.valueFrom} - ${condition?.parameters?.valueTo}`;
                    case 'text_equal_calculated':
                    case 'text_not_equal_calculated':
                    case 'bool_equal_calculated':
                    case 'bool_not_equal_calculated':
                    case 'number_equal_calculated':
                    case 'number_not_equal_calculated':
                    case 'list_equal_calculated':
                    case 'list_not_equal_calculated':
                    case 'multilist_equal_calculated':
                    case 'multilist_not_equal_calculated':
                    case 'datetime_equal_calculated':
                    case 'datetime_not_equal_calculated':
                        return value?.label;
                    default:
                        return value;
                }
        }
    }, [
        condition?.parameters?.value,
        condition?.parameters?.valueFrom,
        condition?.parameters?.valueTo,
        entityFieldsMap,
        field?.type,
        operator,
    ]);

    const [innerValue, setInnerValue] = useState(
        ['list', 'multivalue'].includes(field?.type) ? (
            ''
        ) : (
            <Text isTruncated useTooltip>
                {getValueType(condition?.parameters?.value)}
            </Text>
        ),
    );

    useEffect(() => {
        const value = condition?.parameters?.value;

        if (value?.label) {
            const entityLabel = shouldShowEntity(operator)
                ? entityFieldsMap[value?.value]?.label
                : value?.label;

            setInnerValue([
                <div key={value?.value} className="fm-automation-builder__condition-box__chips">
                    <Chip isFilled>{entityLabel}</Chip>
                </div>,
            ]);
            return;
        }

        if (['int', 'decimal', 'currency'].includes(field?.type)) {
            setInnerValue(getValueType());
            return;
        }

        if (!['list', 'multivalue'].includes(field?.type)) {
            setInnerValue(
                <Text isTruncated useTooltip>
                    {getValueType()}
                </Text>,
            );
            return;
        }

        const list =
            FuzzyMap[field?.tableRelated?.table?.toLowerCase()]?.list || field?.tableRelated?.table;
        getEntityValue(list, Array.isArray(value) ? value : [value])
            .then((result) => {
                if (!result?.data) return;

                const resultValue = value?.map((v) => ({
                    label: result?.data?.[v]?.description,
                    value: result?.data?.[v]?.id,
                }));

                setInnerValue(
                    <div className="fm-automation-builder__condition-box__chips">
                        {resultValue?.map((v) => (
                            <Chip key={v.value} isFilled>
                                {v?.label}
                            </Chip>
                        ))}
                    </div>,
                );
            })
            .catch((e) => {
                console.error(e);
                setInnerValue();
            });
    }, [
        condition,
        entityFieldsMap,
        field?.value,
        field?.tableRelated?.table,
        field?.type,
        getValueType,
        operator,
        field,
    ]);

    return innerValue;
});

export default ConditionBoxValue;
